import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./plans.scss";
import { Box, Button, Modal, Typography } from "@mui/material";
import closex from "../../assets/images/x.png";
import { connect } from "react-redux";
import SnackBar from "../../atoms/snackBar";
import {
  cancelSubscriptionApi,
  getPaymentHistoryListApi,
  getProgramListApi,
  getUserData,
  setUserData,
  paymentCheckoutURLApi,
} from "../../redux/action/user";
import { isPaymentHistoryRowCurrentlyActive } from "../../common";
import "react-quill/dist/quill.snow.css";
import CenterLoading from "../../component/core/CenterLoading";
import CloseIcon from "@mui/icons-material/Close";
import {
  formatProgramPrice,
  getMemberDiscountPercent,
  stripHtml,
} from "../../utils/programPricing";

const isSubscriptionPlan = (plan) =>
  plan?.isSubscription === true ||
  plan?.isSubscription === 1 ||
  plan?.isSubscription === "1" ||
  plan?.isSubscription === "true";

const planDurationMonths = (plan) => {
  const interval = String(plan?.interval || plan?.planType || "").toLowerCase();
  const count = Number(plan?.duration);
  const n = Number.isFinite(count) && count > 0 ? count : 1;
  if (interval.includes("year") || interval.includes("annual")) return 12 * n;
  if (interval.includes("week")) return Math.max(1, Math.round((n * 7) / 30));
  if (interval.includes("day")) return Math.max(1, Math.round(n / 30));
  if (n === 6 || interval.includes("six")) return 6;
  return n;
};

const formatDurationLabel = (plan) => {
  const months = planDurationMonths(plan);
  if (months === 6) return "six months";
  if (months === 1) return "one month";
  if (months === 12) return "one year";
  return `${months} months`;
};

const decodeEntities = (value) => {
  const doc = new DOMParser().parseFromString(String(value || ""), "text/html");
  return doc.documentElement.textContent || "";
};

const decodeMaybeEncodedHtml = (value) => {
  let html = String(value || "");
  if (html.includes("&lt;")) html = decodeEntities(html);
  if (html.includes("&lt;")) html = decodeEntities(html);
  return html;
};

const linesFromHtml = (html) => {
  const doc = new DOMParser().parseFromString(html, "text/html");
  const listItems = [...doc.querySelectorAll("li")]
    .map((el) => el.textContent.replace(/\s+/g, " ").trim())
    .filter(Boolean);
  if (listItems.length) return listItems;

  return [...doc.querySelectorAll("p, h1, h2, h3, h4")]
    .map((el) => el.textContent.replace(/\s+/g, " ").trim())
    .filter((text) => text && !/^membership benefits/i.test(text));
};

const parseIncludes = (includes) => {
  const rawItems = Array.isArray(includes)
    ? includes
    : typeof includes === "string" && includes.trim()
      ? includes.split("|")
      : [];

  const lines = [];
  rawItems.forEach((raw) => {
    const html = decodeMaybeEncodedHtml(raw);
    if (/<[a-z][\s\S]*>/i.test(html)) {
      const extracted = linesFromHtml(html);
      if (extracted.length) {
        lines.push(...extracted);
        return;
      }
    }
    const text = stripHtml(html);
    if (text) lines.push(text);
  });
  return lines;
};

const formatRenewalDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
};

const historyPlanId = (row) =>
  row?.programSubscriptionId ||
  row?.programSubscriptionID ||
  row?.js_programSubscription?.ID ||
  row?.subscriptionId;

const latestHistoryForPlan = (historyList, planId) => {
  if (!planId || !Array.isArray(historyList)) return null;
  const matches = historyList.filter(
    (row) => String(historyPlanId(row)) === String(planId)
  );
  return matches
    .slice()
    .sort((a, b) => {
      const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (aTime !== bTime) return bTime - aTime;
      return (Number(b?.ID) || 0) - (Number(a?.ID) || 0);
    })[0];
};

const isSubscriptionHistoryRow = (row, programList) => {
  if (
    row?.js_programSubscription?.isSubscription === true ||
    row?.js_programSubscription?.isSubscription === 1 ||
    row?.js_programSubscription?.isSubscription === "1"
  ) {
    return true;
  }
  const plan = (programList || []).find(
    (item) => String(item.ID) === String(historyPlanId(row))
  );
  return isSubscriptionPlan(plan);
};

const PlanSubscription = (props) => {
  const navigate = useNavigate();
  const [descModal, setDescModal] = useState(false);
  const [selectedPgrm, setSelectedPgrm] = useState({});
  const [loading, setLoading] = useState(false);
  const [programList, setProgramList] = useState([]);
  const [paymentHistoryList, setPaymentHistoryList] = useState([]);
  const [discountPercent, setDiscountPercent] = useState(20);
  const [subscriptionCancelModal, setSubscriptionCancelModal] = useState(false);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState(null);
  const [openSnackBar, setOpenSnackBar] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const refreshLists = async () => {
    const [programRes, historyRes] = await Promise.all([
      getProgramListApi(),
      getPaymentHistoryListApi(),
    ]);
    const payload = programRes?.data?.data || programRes?.data || {};
    const list = Array.isArray(payload.programList)
      ? payload.programList
      : Array.isArray(payload)
        ? payload
        : [];
    setProgramList(list);
    setDiscountPercent(
      getMemberDiscountPercent(payload.memberProgramDiscountPercent)
    );
    const history = Array.isArray(historyRes?.data?.data)
      ? historyRes.data.data
      : [];
    setPaymentHistoryList(history);
    props.setUserData({ paymentHistoryList: history });
  };

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        await refreshLists();
      } catch (err) {
        if (!cancelled) {
          setOpenSnackBar({
            open: true,
            message: err?.message || "Unable to load memberships.",
            severity: "error",
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sellablePlans = useMemo(
    () =>
      (programList || []).filter((plan) => {
        if (!isSubscriptionPlan(plan)) return false;
        if (plan?.showOnVitalityPortal === false) return false;
        return String(plan?.status) === "1";
      }),
    [programList]
  );

  const currentMembership = useMemo(() => {
    const now = new Date();
    const historyList =
      paymentHistoryList?.length > 0
        ? paymentHistoryList
        : props.user?.paymentHistoryList || [];
    const source =
      Array.isArray(props.user?.subscriptions) && props.user.subscriptions.length > 0
        ? props.user.subscriptions
        : props.user?.subscription
          ? [props.user.subscription]
          : [];

    const fromSubs = source
      .filter((sub) => {
        if (!sub?.isSubscription) return false;
        if (!sub.expirationDate) return true;
        const end = new Date(sub.expirationDate);
        return !Number.isNaN(end.getTime()) && end > now;
      })
      .sort((a, b) => {
        const aEnd = a.expirationDate ? new Date(a.expirationDate).getTime() : 0;
        const bEnd = b.expirationDate ? new Date(b.expirationDate).getTime() : 0;
        return bEnd - aEnd;
      })[0];

    const activeHistory = (historyList || [])
      .filter(
        (row) =>
          isSubscriptionHistoryRow(row, programList) &&
          isPaymentHistoryRowCurrentlyActive(row)
      )
      .sort((a, b) => {
        const aTime = a?.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b?.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      })[0];

    const planId =
      fromSubs?.programSubscriptionId ||
      historyPlanId(activeHistory);

    if (!planId && !fromSubs && !activeHistory) return null;

    const plan = (programList || []).find(
      (item) => String(item.ID) === String(planId)
    );
    const latestHistory = planId
      ? latestHistoryForPlan(historyList, planId)
      : activeHistory;
    const renewalDate =
      fromSubs?.expirationDate || latestHistory?.expirationTime || null;

    return {
      planId,
      name:
        plan?.name ||
        activeHistory?.js_programSubscription?.name ||
        "Membership",
      amount: plan?.amount ?? latestHistory?.js_programSubscription?.amount,
      currency:
        plan?.currency ||
        latestHistory?.js_programSubscription?.currency ||
        "usd",
      renewalDate,
      isCancelled:
        Boolean(fromSubs?.isCancelled) ||
        latestHistory?.orderStatus === "CANCELLED",
      historyId: latestHistory?.ID,
    };
  }, [props.user, programList, paymentHistoryList]);

  const isCurrentSellablePlan = (plan) =>
    currentMembership && String(currentMembership.planId) === String(plan?.ID);

  const handlePayment = async (ele) => {
    try {
      const res = await paymentCheckoutURLApi({
        ID: ele.ID,
      });
      window.location.href = res.data.data.url;
    } catch (error) {
      setOpenSnackBar({
        open: true,
        message: error?.message || "Unable to start checkout.",
        severity: "error",
      });
    }
  };

  const handleCancelSubscription = async (id, isCancelling) => {
    setLoading(true);
    try {
      const res = await cancelSubscriptionApi(
        id || selectedSubscriptionId,
        isCancelling
      );
      setOpenSnackBar({
        open: true,
        message: res?.data?.message,
        severity: "success",
      });
      await props.getUserData(localStorage.getItem("userKey"));
      await refreshLists();
    } catch (error) {
      setOpenSnackBar({
        open: true,
        message: error?.message || "Unable to update membership.",
        severity: "error",
      });
    } finally {
      setSubscriptionCancelModal(false);
      setLoading(false);
    }
  };

  const planMonthlyPrice = (plan) => {
    const months = planDurationMonths(plan);
    return Math.round((Number(plan?.amount) / months) * 100) / 100;
  };

  return (
    <div className="memberships-page">
      <CenterLoading open={loading} />
      <section className="memberships-hero">
        <div className="memberships-hero__inner">
          <p className="memberships-kicker">Memberships</p>
          <h1>Become a member</h1>
          <p className="memberships-hero__lede">
            One membership with six months of full access: your complete Vitality
            Score results, community, our doctors, and member pricing on labs and
            programs.
          </p>

          <div className="membership-status">
            <div>
              <p className="membership-status__kicker">Your current membership</p>
              <h2>{currentMembership ? currentMembership.name : "Free plan"}</h2>
              {currentMembership ? (
                <p className="membership-status__meta">
                  {formatProgramPrice(
                    currentMembership.amount,
                    currentMembership.currency
                  )}
                  {currentMembership.renewalDate
                    ? ` · ${
                        currentMembership.isCancelled ? "Access until" : "Renews"
                      } ${formatRenewalDate(currentMembership.renewalDate)}`
                    : ""}
                </p>
              ) : null}
            </div>
            {currentMembership &&
            !currentMembership.isCancelled &&
            currentMembership.historyId ? (
              <button
                type="button"
                className="membership-status__cancel"
                onClick={() => {
                  setSelectedSubscriptionId(currentMembership.historyId);
                  setSubscriptionCancelModal(true);
                }}
              >
                Cancel membership
              </button>
            ) : null}
          </div>
        </div>
      </section>

      {sellablePlans.length > 0 ? (
        <section className="membership-offer">
          {sellablePlans.map((plan) => {
            const includes = parseIncludes(plan.includes);
            const monthly = planMonthlyPrice(plan);
            return (
              <article className="membership-card" key={plan.ID}>
                <div className="membership-card__banner">
                  <h2>{plan.name}</h2>
                </div>
                <div className="membership-card__body">
                  <div className="membership-card__price-col">
                    <p className="membership-card__price">
                      {formatProgramPrice(plan.amount, plan.currency)}
                      <span> for {formatDurationLabel(plan)}</span>
                    </p>
                    <p className="membership-card__monthly">
                      One payment. Works out to{" "}
                      {formatProgramPrice(monthly, plan.currency)} a month.
                    </p>
                  </div>
                  <div className="membership-card__includes">
                    <p className="membership-card__includes-kicker">
                      What is included
                    </p>
                    {includes.length > 0 ? (
                      <ul>
                        {includes.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    ) : plan.description ? (
                      <p>{stripHtml(plan.description)}</p>
                    ) : null}
                  </div>
                  <div className="membership-card__actions">
                    {isCurrentSellablePlan(plan) ? (
                      <button
                        type="button"
                        className="membership-card__cta is-current"
                        disabled
                      >
                        Current plan
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="membership-card__cta"
                        onClick={() => handlePayment(plan)}
                      >
                        Become a member
                      </button>
                    )}
                    {currentMembership ? (
                      <button
                        type="button"
                        className="membership-card__details"
                        onClick={() => {
                          setSelectedPgrm(plan);
                          setDescModal(true);
                        }}
                      >
                        Details
                      </button>
                    ) : null}
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      ) : null}

      <section className="membership-programs">
        <div className="membership-programs__inner">
          <div>
            <h2>Programs and courses</h2>
            <p>
              Go deeper with a program whenever you are ready. Members save{" "}
              {getMemberDiscountPercent(discountPercent)}% on digital products,
              courses and ebooks.
            </p>
          </div>
          <button type="button" onClick={() => navigate("/courses")}>
            Browse programs
          </button>
        </div>
      </section>

      <Modal open={descModal} onClose={() => setDescModal(false)}>
        <Box sx={modalStyle}>
          <div className="close-pop-wrap">
            <img
              src={closex}
              alt="Close"
              onClick={() => setDescModal(false)}
            />
          </div>
          <div>
            {selectedPgrm?.summary ? (
              <HtmlContent rawHtmlContent={selectedPgrm.summary} />
            ) : (
              <Typography p={2}>
                {stripHtml(selectedPgrm?.description) || "No details available."}
              </Typography>
            )}
          </div>
        </Box>
      </Modal>

      <Modal
        open={subscriptionCancelModal}
        onClose={() => setSubscriptionCancelModal(false)}
      >
        <Box
          sx={{ ...cancelSubscriptionModalStyle }}
          width={"45%"}
          maxWidth={"600px"}
          minWidth={"350px"}
        >
          <box className="close-pop-wrap">
            <Box className="Heading">
              <Typography variant="h6" sx={{ fontWeight: "bold", color: "#fff" }}>
                Cancel membership
              </Typography>
            </Box>
            <CloseIcon
              onClick={() => setSubscriptionCancelModal(false)}
              className="CloseImg"
            />
          </box>
          <div className="ContentWrapper">
            <Typography>
              You will continue to enjoy the benefits of being a subscribed
              member till the end of the current billing cycle. Thereafter, your
              account will have limited features until you renew again. Do you
              wish to proceed?
            </Typography>
          </div>
          <Box
            className="modal-actions"
            display="flex"
            justifyContent="end"
            mt={2}
            marginRight={2}
          >
            <Button
              variant="outlined"
              color="secondary"
              sx={{ marginRight: 2 }}
              onClick={() =>
                handleCancelSubscription(selectedSubscriptionId, true)
              }
            >
              Yes
            </Button>
            <Button
              variant="contained"
              sx={{
                fontWeight: "bold",
                fontSize: "16px",
                background: "#FEBD36",
                color: "#1a1a14",
                "&:hover": { background: "#E5A82B" },
              }}
              onClick={() => setSubscriptionCancelModal(false)}
            >
              No
            </Button>
          </Box>
        </Box>
      </Modal>

      <SnackBar
        open={openSnackBar.open}
        message={openSnackBar.message}
        severity={openSnackBar.severity}
        handleCloseSnackBar={() => {
          setOpenSnackBar({
            open: false,
            message: "",
            severity: "",
          });
        }}
      />
    </div>
  );
};

const actionCreators = {
  getUserData,
  setUserData,
};

export default connect(
  ({ user }) => ({
    user,
  }),
  actionCreators
)(PlanSubscription);

const HtmlContent = ({ rawHtmlContent }) => {
  const decodedHtml = decodeHtmlEntities(rawHtmlContent);
  return <div dangerouslySetInnerHTML={{ __html: decodedHtml }} />;
};

export function decodeHtmlEntities(text) {
  const doc = new DOMParser().parseFromString(text || "", "text/html");
  return doc.documentElement.textContent || doc.documentElement.innerText;
}

const modalStyle = {
  overflow: "hidden",
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "80%",
  maxHeight: "90vh",
  backgroundColor: "white",
  boxShadow: 24,
  padding: "20px",
  borderRadius: "8px",
  ".close-pop-wrap": {
    top: "0px",
    right: "0px",
    position: "sticky",
    padding: "10px",
    textAlign: "right",
    img: {
      width: "24px",
      height: "24px",
      cursor: "pointer",
    },
  },
};

const cancelSubscriptionModalStyle = {
  overflow: "hidden",
  position: "relative",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90vh",
  backgroundColor: "white",
  boxShadow: 24,
  pb: 2,
  borderRadius: "8px",
  ".close-pop-wrap": {
    top: "0px",
    right: "0px",
    position: "sticky",
    padding: "10px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#112225",
  },
  ".Heading": {
    display: "flex",
    alignItems: "center",
    h2: {
      mb: 0,
      color: "#fff",
    },
  },
  ".CloseImg": {
    opacity: 1,
    borderRadius: "50%",
    p: "2px",
    border: "1px solid #fff",
    fontSize: "0.7rem",
    path: {
      fill: "#fff",
    },
  },
  ".ContentWrapper": {
    p: 2,
  },
};
